import { useState } from "react";
import { Link } from "react-router-dom";
import type { Chapter } from "../api";
import ConfirmDelete from "../components/ConfirmDelete";
import { TitleInput } from "../components/EditableTitle";
import { useT } from "../i18n";
import type { DragHandle } from "./DragList";

/**
 * ChapterRow is a whole-row link to the chapter.
 *
 * The link is stretched over the row with an ::after overlay rather than the row
 * carrying an onClick: that keeps one link in the accessibility tree, keeps
 * middle-click and "copy link address" working, and keeps the title as the
 * accessible name. The controls sit above the overlay on their own stacking
 * context, or the overlay would swallow every tap on them.
 *
 * Renaming is triggered from the controls rather than from the title, because
 * the title is the link — a click cannot both navigate and start an edit.
 */
export default function ChapterRow({
  chapter,
  writable,
  renamable = writable,
  studio = false,
  handle,
  onDelete,
  onRename,
}: {
  chapter: Chapter;
  /** Whether the row shows its controls: rename, move and delete. */
  writable: boolean;
  /** Whether it offers the rename, which a linked book keeps when it loses
      the rest (ADR-0098 §1). Defaults to writable. */
  renamable?: boolean;
  /** Whether the row links to the studio and says its narration state. */
  studio?: boolean;
  /** What the row is dragged by, from DragList. */
  handle: DragHandle;
  onDelete: () => void;
  onRename: (title: string) => Promise<unknown>;
}) {
  const [renaming, setRenaming] = useState(false);
  const t = useT();

  return (
    <div
      data-testid="chapter-row"
      className="relative flex items-center gap-3 rounded-card border border-line bg-surface px-4 py-3 transition-colors hover:border-line-strong focus-within:border-sage"
    >
      {/* Where a chapter leads depends on whose book it is. If you can edit this
          book you get the studio view, with the narration controls and the
          corrections; if you cannot, you get the reader. Nobody is ever shown a
          Narrate button for a book they do not own. */}
      <div className="min-w-0 flex-1">
        {renaming ? (
          <TitleInput
            value={chapter.title}
            onSave={onRename}
            onDone={() => setRenaming(false)}
            label={t.book.chapterNoun}
            className="font-medium"
            testId="chapter-title-input"
          />
        ) : (
          <Link
            to={studio ? `/studio/chapters/${chapter.id}` : `/read/${chapter.id}`}
            dir="auto"
            className="font-medium after:absolute after:inset-0 after:content-[''] hover:text-sage-deep"
          >
            {chapter.title}
          </Link>
        )}
        <p className="text-xs text-muted">
          {t.common.characters(chapter.char_count)}
          {/* In the studio the state that matters is the narration's, not the
              revision number nobody acts on. */}
          {studio ? (
            <ChapterState chapter={chapter} />
          ) : chapter.revision_no ? (
            t.book.revision(chapter.revision_no)
          ) : (
            ""
          )}
        </p>
      </div>
      {(writable || renamable) && !renaming && (
        <div className="relative z-10 flex shrink-0 items-center gap-1">
          {renamable && (
            <IconButton
              label={t.book.renameChapter}
              testID="rename-chapter"
              onClick={() => setRenaming(true)}
            >
              ✎
            </IconButton>
          )}
          {writable && (
            <>
              {/* The grip, not a pair of arrows. It is the last control in the row
              and the one furthest from the title, because it is the one a
              finger goes looking for and holds. */}
              <button
                type="button"
                {...handle}
                className="cursor-grab rounded-control border border-line px-2 py-1 text-muted active:cursor-grabbing disabled:opacity-30"
              >
                <GripIcon />
              </button>
              <ConfirmDelete
                compact
                testId="delete-chapter"
                label={t.book.deleteChapter}
                question={t.book.deleteChapterQuestion}
                onConfirm={onDelete}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Six dots: the grip every list on every phone uses to mean "drag me". */
function GripIcon() {
  return (
    <svg viewBox="0 0 16 16" className="size-4" fill="currentColor" aria-hidden="true">
      <circle cx="6" cy="3.5" r="1.4" />
      <circle cx="10" cy="3.5" r="1.4" />
      <circle cx="6" cy="8" r="1.4" />
      <circle cx="10" cy="8" r="1.4" />
      <circle cx="6" cy="12.5" r="1.4" />
      <circle cx="10" cy="12.5" r="1.4" />
    </svg>
  );
}

function IconButton({
  label,
  testID,
  onClick,
  children,
}: {
  label: string;
  testID: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      data-testid={testID}
      onClick={onClick}
      disabled={!onClick}
      className="rounded-control border border-line px-2 py-1 text-xs disabled:opacity-30"
    >
      {children}
    </button>
  );
}

/**
 * ChapterState is one word on the narration: narrating, failed, passages
 * missing, not yet, behind, done.
 */
function ChapterState({ chapter }: { chapter: Chapter }) {
  const t = useT();
  const warn = "text-amber-700 dark:text-amber-400";
  // Holes in the audio before "narrated": a chapter that plays with passages
  // missing said "Narrated" and was only found by opening it (ADR-0120).
  const [state, tone] = chapter.narrating
    ? [t.book.stateNarrating, "text-sage-deep"]
    : chapter.narration_failed
      ? [t.book.stateNarrationFailed, warn]
      : chapter.missing_passages
        ? [t.book.stateMissing(chapter.missing_passages), warn]
        : !chapter.has_active_render
          ? [t.book.stateNotNarrated, "text-muted"]
          : chapter.audio_outdated
            ? [t.book.stateAudioOutdated, warn]
            : [t.book.stateNarrated, "text-muted"];
  return (
    <span data-testid="chapter-state" data-state={state} className={tone}>
      {" · "}
      {state}
    </span>
  );
}
